import axios from "axios";
import { BaseUrl } from "../../Environment";
import { getSecureItem } from "../../utilsFunctions";

// ─────────────────────────────────────────────
// Shared Axios instance
// Automatically attaches the latest auth token
// to every request.
// ─────────────────────────────────────────────
const ticketApi = axios.create({
  baseURL: `${BaseUrl}/ticket`,
});

ticketApi.interceptors.request.use((config) => {
  const token = getSecureItem("token");

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// ─────────────────────────────────────────────
// Common request wrapper
// Returns the axios response on success and
// error.response on failure to keep API handling
// consistent throughout the app.
// ─────────────────────────────────────────────
const request = async (callback, label) => {
  try {
    return await callback();
  } catch (error) {
    console.error(`${label}:`, error);
    return error.response;
  }
};

// ─────────────────────────────────────────────
// Add Contact
// POST /ticket/postContact
// ─────────────────────────────────────────────
export const addContactApi = (data) =>
  request(() => ticketApi.post("/postContact", data), "addContactApi");

// ─────────────────────────────────────────────
// Create Ticket
// POST /ticket/postTicket
// ─────────────────────────────────────────────
export const createTicketApi = (data) =>
  request(() => ticketApi.post("/postTicket", data), "createTicketApi");

// ─────────────────────────────────────────────
// Get Tickets By Contact
// GET /ticket/getTicketsByContact/:contactId
// ─────────────────────────────────────────────
export const getTicketsByContactApi = (contactId, params = {}) =>
  request(
    () => ticketApi.get(`/getTicketsByContact/${contactId}`, { params }),
    "getTicketsByContactApi",
  );

// ─────────────────────────────────────────────
// Get Ticket By ID
// GET /ticket/getTicketById/:ticketId
// ─────────────────────────────────────────────
export const getTicketByIdApi = (ticketId, params = {}) =>
  request(
    () => ticketApi.get(`/getTicketById/${ticketId}`, { params }),
    "getTicketByIdApi",
  );

// ─────────────────────────────────────────────
// Get Complete Ticket Conversation
// Returns merged threads, replies, comments
// and attachment metadata.
// GET /ticket/getTicketConversation/:ticketId
// ─────────────────────────────────────────────
export const getTicketConversationApi = (ticketId) =>
  request(
    () => ticketApi.get(`/getTicketConversation/${ticketId}`),
    "getTicketConversationApi",
  );

// ─────────────────────────────────────────────
// Post Ticket Reply
// Sends a text reply from the end user.
// POST /ticket/postTicketReply/:ticketId
// ─────────────────────────────────────────────
export const postTicketReplyApi = (ticketId, message) =>
  request(
    () => ticketApi.post(`/postTicketReply/${ticketId}`, { message }),
    "postTicketReplyApi",
  );

// ─────────────────────────────────────────────
// Upload Ticket Attachment
// Uploads a single file to an existing ticket.
// POST /ticket/tickets/:ticketId/attachments
// multipart/form-data
// ─────────────────────────────────────────────
export const postTicketAttachmentApi = (ticketId, file) => {
  const formData = new FormData();
  formData.append("file", file);

  return request(
    () =>
      ticketApi.post(`/tickets/${ticketId}/attachments`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
    "postTicketAttachmentApi",
  );
};

// ─────────────────────────────────────────────
// Download Ticket Attachment
// Downloads an attachment by its attachment ID.
// Returns a Blob response for browser download.
// GET /ticket/attachment/:ticketId/:attachmentId
// ─────────────────────────────────────────────
export const getTicketAttachmentApi = (ticketId, attachmentId) =>
  request(
    () =>
      ticketApi.get(`/attachment/${ticketId}/${attachmentId}`, {
        responseType: "blob",
      }),
    "getTicketAttachmentApi",
  );

// ─────────────────────────────────────────────
// Create Lead
// POST /ticket/createLead
// ─────────────────────────────────────────────
export const createLeadApi = (data) =>
  request(() => ticketApi.post("/createLead", data), "createLeadApi");